/**
 * StateWidget component.
 *
 * Shows a searchable select of all US states when the country picked elsewhere
 * in the same form (`state.form.global.country`) is "United States"; otherwise
 * falls back to a plain text input (same markup / styling as the customised
 * TextWidget). The value is stored as the state / free-text string.
 *
 * @module components/Widgets/StateWidget/StateWidget
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { Input } from 'semantic-ui-react';

import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import {
  Option,
  ClearIndicator,
  DropdownIndicator,
  MultiValueContainer,
  selectTheme,
  customSelectStyles,
  MenuList,
} from '@plone/volto/components/manage/Widgets/SelectStyling';

import clearSVG from '@plone/volto/icons/clear.svg';

import usStates from './usStates';

const messages = defineMessages({
  select: {
    id: 'Select…',
    defaultMessage: 'Select…',
  },
});

const US_COUNTRY = 'United States';

const stateOptions = usStates.map(({ name, code }) => ({
  value: code,
  label: `${name} (${code})`,
}));

const StateWidget = (props) => {
  const {
    id,
    title,
    value,
    onChange,
    onBlur,
    isDisabled,
    disabled,
    required,
    placeholder,
    error,
    resettable,
    reactSelect,
  } = props;

  const intl = useIntl();
  const country = useSelector((state) => state.form?.global?.country);
  const isUS = country === US_COUNTRY;

  const onReset = () => onChange(id, null);

  if (isUS) {
    const Select = reactSelect.default;
    const selectedValue =
      stateOptions.find((option) => option.value === value) || null;

    return (
      <FormFieldWrapper {...props} className="text">
        <Select
          id={`field-${id}`}
          name={id}
          aria-label={title || undefined}
          isDisabled={isDisabled || disabled}
          isClearable={!required}
          isSearchable
          menuShouldScrollIntoView={false}
          menuPlacement="auto"
          maxMenuHeight={1600}
          className="react-select-container"
          classNamePrefix="react-select"
          options={stateOptions}
          styles={customSelectStyles}
          theme={selectTheme}
          components={{
            MenuList,
            MultiValueContainer,
            ClearIndicator,
            DropdownIndicator,
            Option,
          }}
          value={selectedValue}
          placeholder={placeholder ?? intl.formatMessage(messages.select)}
          onBlur={() => onBlur && onBlur(id, value)}
          onChange={(selected) =>
            onChange(id, selected ? selected.value : undefined)
          }
        />
      </FormFieldWrapper>
    );
  }

  return (
    <FormFieldWrapper {...props} className="text">
      <div className="date-time-widget-wrapper text-widget-wrapper">
        <Input
          id={`field-${id}`}
          name={id}
          value={value || ''}
          disabled={isDisabled || disabled}
          placeholder={placeholder}
          onChange={({ target }) =>
            onChange(id, target.value === '' ? undefined : target.value)
          }
          onBlur={({ target }) =>
            onBlur && onBlur(id, target.value === '' ? undefined : target.value)
          }
          aria-required={required ? 'true' : undefined}
          aria-invalid={error?.length > 0 ? 'true' : undefined}
        />
        {resettable && value && (
          <button
            type="button"
            disabled={isDisabled || disabled}
            onClick={onReset}
            className="item ui noborder button"
            aria-label="Clear"
          >
            <Icon name={clearSVG} size="24px" className="close" />
          </button>
        )}
      </div>
    </FormFieldWrapper>
  );
};

StateWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  wrapped: PropTypes.bool,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  resettable: PropTypes.bool,
};

StateWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  resettable: true,
};

export default injectLazyLibs(['reactSelect'])(StateWidget);
